import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowRight, CalendarDays, Clock3 } from 'lucide-react';
import { guidesAPI } from '../services/api';
import { PUBLIC_CONTENT_PAGE_MAX_WIDTH } from '../utils/publicPageLayout';
import { stripHtmlToText, usePageSeo } from '../utils/seo';
import { appUrl } from '../utils/appPaths';

const GuidesContainer = styled.div`
  width: min(${PUBLIC_CONTENT_PAGE_MAX_WIDTH}, calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
  padding: clamp(24px, 4vw, 48px) 0 clamp(24px, 3vw, 36px);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
  margin-top: 20px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #64748b;
`;

const GuidesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 22px;
`;

const GuideCard = styled(Link)`
  display: grid;
  align-content: start;
  gap: 16px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  background: white;
  border-radius: 24px;
  border: 1px solid #dbe4ea;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: #8dd6cb;
    box-shadow: 0 22px 48px rgba(15, 23, 42, 0.12);
  }
`;

const GuideCardImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  background: linear-gradient(135deg, #d6f6f0 0%, #eff6ff 100%);
`;

const GuideCardBody = styled.div`
  padding: 0 20px 22px;
  display: grid;
  gap: 14px;
`;

const GuideCardTitle = styled.h2`
  color: #0f172a;
  font-size: 1.35rem;
  line-height: 1.2;
`;

const GuideCardExcerpt = styled.p`
  color: #475569;
  font-size: 0.98rem;
  line-height: 1.7;
`;

const GuideCardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const GuideMetaPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #f8fafc;
  color: #334155;
  font-size: 13px;
  font-weight: 500;
`;

const GuideCardCta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0f766e;
  font-weight: 700;
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;
  color: #64748b;
  font-size: 1.1rem;
`;

const ErrorState = styled.div`
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border-radius: 20px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 1rem;
`;

const EmptyState = styled.div`
  min-height: 280px;
  display: grid;
  place-items: center;
  text-align: center;
  padding: 32px;
  border-radius: 20px;
  background: white;
  border: 1px solid #e2e8f0;
  color: #64748b;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 0.9rem;
`;

const formatGuideDate = (value) => {
  if (!value) {
    return '';
  }

  const normalizedValue = String(value).includes('T')
    ? value
    : `${String(value).replace(' ', 'T')}Z`;
  const parsedDate = new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return parsedDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const estimateReadTime = (value) => {
  const words = stripHtmlToText(value).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
};

const buildGuideExcerpt = (guide) => {
  const explicitExcerpt = stripHtmlToText(guide?.excerpt);
  if (explicitExcerpt) {
    return explicitExcerpt;
  }

  const contentExcerpt = stripHtmlToText(guide?.content_html);
  return contentExcerpt.length > 180
    ? `${contentExcerpt.slice(0, 177).trimEnd()}...`
    : contentExcerpt;
};

const Guides = () => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  usePageSeo({
    title: 'Guides | MicroPinouts',
    description: 'Electronics articles, tutorials, and walkthroughs for makers using MicroPinouts.',
    keywords: 'electronics guides, maker tutorials, microcontroller articles, micropinouts guides',
    canonicalUrl: appUrl('/guides'),
    ogType: 'website',
  });

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        setLoading(true);
        const response = await guidesAPI.getAll();
        setGuides(Array.isArray(response.data) ? response.data : []);
        setError('');
      } catch (fetchError) {
        console.error('Failed to fetch guides:', fetchError);
        setError('Failed to load guides.');
      } finally {
        setLoading(false);
      }
    };

    fetchGuides();
  }, []);

  const guideCards = useMemo(
    () => guides.map((guide) => ({
      ...guide,
      excerptPreview: buildGuideExcerpt(guide),
      publishedLabel: formatGuideDate(guide.published_at || guide.created_at),
      readTimeMinutes: estimateReadTime(guide.content_html || guide.excerpt || ''),
    })),
    [guides]
  );

  if (loading) {
    return (
      <GuidesContainer>
        <LoadingState>Loading guides...</LoadingState>
      </GuidesContainer>
    );
  }

  if (error) {
    return (
      <GuidesContainer>
        <ErrorState>{error}</ErrorState>
      </GuidesContainer>
    );
  }

  return (
    <GuidesContainer>
      <Header>
        <Title>Guides</Title>
        <Subtitle>
          Browse practical walkthroughs, reference writeups, and project notes designed to help makers move from pinout lookup to a working build.
        </Subtitle>
      </Header>

      {guideCards.length === 0 ? (
        <EmptyState>
          <div>
            <h3 style={{ marginBottom: '8px', color: '#334155' }}>No guides published yet</h3>
            <p>Check back soon for new articles and walkthroughs.</p>
          </div>
        </EmptyState>
      ) : (
        <GuidesGrid>
          {guideCards.map((guide) => (
            <GuideCard key={guide.id} to={`/guides/${guide.slug}`}>
              {guide.cover_image_url && (
                <GuideCardImage src={guide.cover_image_url} alt={guide.title} loading="lazy" />
              )}

              <GuideCardBody>
                <GuideCardTitle>{guide.title}</GuideCardTitle>
                {guide.excerptPreview && <GuideCardExcerpt>{guide.excerptPreview}</GuideCardExcerpt>}

                <GuideCardMeta>
                  {guide.publishedLabel && (
                    <GuideMetaPill>
                      <CalendarDays size={14} />
                      {guide.publishedLabel}
                    </GuideMetaPill>
                  )}

                  <GuideMetaPill>
                    <Clock3 size={14} />
                    {`${guide.readTimeMinutes} min read`}
                  </GuideMetaPill>
                </GuideCardMeta>

                <GuideCardCta>
                  Read Guide
                  <ArrowRight size={16} />
                </GuideCardCta>
              </GuideCardBody>
            </GuideCard>
          ))}
        </GuidesGrid>
      )}

      <Footer>
        <strong>&copy; 2026 micropinouts.com</strong><br />
        All rights reserved.<br />
        Educational use and community sharing encouraged under applicable open-source licenses.
      </Footer>
    </GuidesContainer>
  );
};

export default Guides;
